
import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18n from '../../../../i18n/i18n';
import { getCustomerDetails, updateCustomer, clearError, clearSuccess, clearCustomerDetails } from '../../../../redux/Slices/authSlice';
import { toast } from 'react-toastify';
import { useTitle } from '../../../../shared/hooks/TitleContext';
import { makeOfflineRequest } from '../../../../shared/utils.js/offlineRequestManager';
import { saveCustomersCache, getCustomersCache, generateCustomersCacheKey, getAllCustomersCacheForQuery, isOnline } from '../../../../shared/utils.js/offlineQueue';
import { applyFieldFilter } from '../../../../shared/utils.js/fieldValidation';


const EditCustomer = () => {
  const { t } = useTranslation('global');
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { setTitle } = useTitle();
  const { id } = useParams();
  const { customerDetails, isLoading, error, success } = useSelector((state) => state.auth);
  const lastErrorRef = useRef({ message: null, time: 0 });
  const lastSuccessRef = useRef({ message: null, time: 0 });

  // Always fetch customer details from API when online, and cache it
  useEffect(() => {
    const fetchAndCache = async () => {
      if (isOnline() && id) {
        try {
          const result = await dispatch(getCustomerDetails(id)).unwrap();
          const customerData = result?.data || result;

          if (customerData && customerData.id) {
            try {
              const allCached = await getAllCustomersCacheForQuery({ per_page: 1000 });
              let updatedList = [...allCached];
              const index = updatedList.findIndex(c => String(c.id) === String(customerData.id));
              if (index !== -1) {
                updatedList[index] = { ...updatedList[index], ...customerData };
              } else {
                updatedList.push(customerData);
              }

              // Save to a "general" offline cache key
              const cacheKey = generateCustomersCacheKey({ page: 1, per_page: 1000 });
              await saveCustomersCache(cacheKey, updatedList, {
                current_page: 1,
                last_page: 1,
                per_page: updatedList.length,
                total: updatedList.length
              });
            } catch (cacheErr) {
              console.error("Failed to cache customer details", cacheErr);
            }
          }
        } catch (e) {
          // If fetch fails, we rely on the other useEffect to load from cache
          console.error("Online fetch failed", e);
        }
      }
    };
    fetchAndCache();
  }, [id, dispatch]);
  const isRTL = i18n.language === 'ar';
  const textAlign = isRTL ? 'right' : 'left';
  const dir = isRTL ? 'rtl' : 'ltr';

  const [formData, setFormData] = useState({
    name_en: '',
    name_ar: '',
    code: '',
    contact_person: '',
    email: '',
    phone: '',
    address: '',
    tax_number: '',
    is_active: true,
    notes: ''
  });

  // Always try to load customer details from cache first, then fallback to Redux state (API)
  const [hasShownNoDataToast, setHasShownNoDataToast] = useState(false);
  useEffect(() => {
    const fillFromCacheOrRedux = async () => {
      try {
        const allCached = await getAllCustomersCacheForQuery({ per_page: 1000 });
        const cachedCustomer = allCached.find(c => String(c.id) === String(id));
        if (cachedCustomer) {
          setFormData({
            name_en: cachedCustomer.name_en || cachedCustomer.name || '',
            name_ar: cachedCustomer.name_ar || cachedCustomer.name || '',
            code: cachedCustomer.code || '',
            contact_person: cachedCustomer.contact_person || '',
            email: cachedCustomer.email || '',
            phone: cachedCustomer.phone || '',
            address: cachedCustomer.address || '',
            tax_number: cachedCustomer.tax_number || '',
            is_active: cachedCustomer.is_active !== undefined ? cachedCustomer.is_active : true,
            notes: cachedCustomer.notes || ''
          });
          if (!isOnline()) {
            toast.info(t('customers.messages.cacheLoaded'), { rtl: isRTL });
          }
          setHasShownNoDataToast(false);
        } else if (customerDetails) {
          setFormData({
            name_en: customerDetails.name_en || customerDetails.name || '',
            name_ar: customerDetails.name_ar || customerDetails.name || '',
            code: customerDetails.code || '',
            contact_person: customerDetails.contact_person || '',
            email: customerDetails.email || '',
            phone: customerDetails.phone || '',
            address: customerDetails.address || '',
            tax_number: customerDetails.tax_number || '',
            is_active: customerDetails.is_active !== undefined ? customerDetails.is_active : true,
            notes: customerDetails.notes || ''
          });
          setHasShownNoDataToast(false);
        } else if (
          !isLoading &&
          !hasShownNoDataToast &&
          // Check both cache and Redux for valid data
          (!customerDetails ||
            (
              !customerDetails.id &&
              !customerDetails.name &&
              !customerDetails.name_en &&
              !customerDetails.name_ar
            )
          ) &&
          // Also check cache result
          (!cachedCustomer ||
            (
              !cachedCustomer.id &&
              !cachedCustomer.name &&
              !cachedCustomer.name_en &&
              !cachedCustomer.name_ar
            )
          ) &&
          // If online, don't show "No Data" unless there's an actual error (wait for API)
          (!isOnline() || error)
        ) {
          toast.error(t('customers.messages.noData'), { rtl: isRTL });
          setHasShownNoDataToast(true);
        }
      } catch (err) {
        if (!hasShownNoDataToast) {
          toast.error(t('customers.messages.loadError'), { rtl: isRTL });
          setHasShownNoDataToast(true);
        }
      }
    };
    fillFromCacheOrRedux();
  }, [customerDetails, id, isLoading, hasShownNoDataToast, error]);

  // Handle success messages
  useEffect(() => {
    if (success) {
      const now = Date.now();
      const last = lastSuccessRef.current;
      if (!last.message || last.message !== success || now - last.time > 2000) {
        toast.success(success, {
          rtl: isRTL,
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true
        });
        lastSuccessRef.current = { message: success, time: now };
      }
      setTimeout(() => {
        dispatch(clearSuccess());
        navigate('/customers');
      }, 1500);
    }
  }, [success, dispatch, navigate, isRTL]);

  // Handle error messages
  useEffect(() => {
    if (error) {
      const now = Date.now();
      const last = lastErrorRef.current;
      if (!last.message || last.message !== error || now - last.time > 2000) {
        toast.error(error, {
          rtl: isRTL,
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true
        });
        lastErrorRef.current = { message: error, time: now };
      }
      setTimeout(() => dispatch(clearError()), 3000);
    }
  }, [error, dispatch, isRTL]);

  // Handles input changes for all form fields
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const finalValue = type === 'checkbox' ? checked : applyFieldFilter(name, value);
    setFormData(prev => ({ ...prev, [name]: finalValue }));
  };

  // ...existing code...

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isOnline()) {
      try {
        await makeOfflineRequest(`/customers/${id}`, 'PUT', formData);
      } catch (err) {
        if (err.message === 'OFFLINE_REQUEST_SAVED' || err.isOffline) {
          // Optimistically update cache
          try {
            const allCached = await getAllCustomersCacheForQuery({ per_page: 1000 });
            let updatedList = [...allCached];
            const index = updatedList.findIndex(c => String(c.id) === String(id));

            const optimisticCustomer = {
              ...(index !== -1 ? updatedList[index] : {}),
              id: Number(id),
              ...formData,
              name: formData.name_en
            };

            if (index !== -1) {
              updatedList[index] = optimisticCustomer;
            } else {
              updatedList.push(optimisticCustomer);
            }

            const cacheKey = generateCustomersCacheKey({ page: 1, per_page: 1000 });
            await saveCustomersCache(cacheKey, updatedList, {
              current_page: 1,
              last_page: 1,
              per_page: updatedList.length,
              total: updatedList.length
            });

            toast.info(t('customers.messages.savedLocally'), { rtl: isRTL });
            setTimeout(() => navigate('/customers'), 1500);
          } catch (cacheErr) {
            console.error('Optimistic cache update failed', cacheErr);
            toast.error(t('customers.messages.cacheUpdateFailed'), { rtl: isRTL });
          }
          return;
        }
      }
    }

    try {
      await dispatch(updateCustomer({ id, data: formData })).unwrap();
      // Success and error handling is done by useEffect
    } catch (error) {
      // Error handling is done by useEffect
    }
  };

  if (isLoading && !customerDetails) {
    return (
      <div style={{
        padding: '30px',
        backgroundColor: '#14263b',
        minHeight: 'calc(100vh - 80px)',
        color: 'white',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        direction: isRTL ? 'rtl' : 'ltr'
      }}>
        {t('customers.edit.loading')}
      </div>
    );
  }

  return (
    <div style={{
      padding: '30px',
      backgroundColor: '#14263b',
      minHeight: 'calc(100vh - 80px)',
      color: 'white',
      direction: dir
    }}>
      <div style={{ marginBottom: '30px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexDirection: isRTL ? 'row-reverse' : 'row' }}>
        <h1 style={{ fontSize: '24px', fontWeight: 'bold', margin: 0 }}>{t('customers.edit.title')}</h1>
        <button
          onClick={() => navigate('/customers')}
          style={{
            backgroundColor: '#666',
            color: 'white',
            border: 'none',
            padding: '10px 18px',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '14px'
          }}
        >
          {t('customers.edit.back')}
        </button>
      </div>

      <form onSubmit={handleSubmit} style={{
        backgroundColor: '#1b3350',
        border: '1px solid #333',
        borderRadius: '12px',
        padding: '32px',
        width: '100%',
        maxWidth: '100%',
        textAlign: isRTL ? 'right' : 'left'
      }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
          {isRTL ? (
            <>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 'bold', color: 'white' }}>
                  {t('customers.edit.nameAr')} <span style={{ color: '#dc3545' }}>*</span>
                </label>
                <input
                  type="text"
                  name="name_ar"
                  value={formData.name_ar}
                  onChange={handleChange}
                  required
                  style={{
                    width: '100%',
                    padding: '12px',
                    backgroundColor: '#1a1f2e',
                    border: '1px solid #333',
                    borderRadius: '8px',
                    color: 'white',
                    fontSize: '14px',
                    boxSizing: 'border-box',
                    direction: 'rtl',
                    textAlign: 'right'
                  }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 'bold', color: 'white' }}>
                  {t('customers.edit.nameEn')} <span style={{ color: '#dc3545' }}>*</span>
                </label>
                <input
                  type="text"
                  name="name_en"
                  value={formData.name_en}
                  onChange={handleChange}
                  required
                  style={{
                    width: '100%',
                    padding: '12px',
                    backgroundColor: '#1a1f2e',
                    border: '1px solid #333',
                    borderRadius: '8px',
                    color: 'white',
                    fontSize: '14px',
                    boxSizing: 'border-box',
                    direction: 'ltr',
                    textAlign: 'left'
                  }}
                />
              </div>
            </>
          ) : (
            <>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 'bold', color: 'white' }}>
                  {t('customers.edit.nameEn')} <span style={{ color: '#dc3545' }}>*</span>
                </label>
                <input
                  type="text"
                  name="name_en"
                  value={formData.name_en}
                  onChange={handleChange}
                  required
                  style={{
                    width: '100%',
                    padding: '12px',
                    backgroundColor: '#1a1f2e',
                    border: '1px solid #333',
                    borderRadius: '8px',
                    color: 'white',
                    fontSize: '14px',
                    boxSizing: 'border-box',
                    direction: 'ltr',
                    textAlign: 'left'
                  }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 'bold', color: 'white' }}>
                  {t('customers.edit.nameAr')} <span style={{ color: '#dc3545' }}>*</span>
                </label>
                <input
                  type="text"
                  name="name_ar"
                  value={formData.name_ar}
                  onChange={handleChange}
                  required
                  style={{
                    width: '100%',
                    padding: '12px',
                    backgroundColor: '#1a1f2e',
                    border: '1px solid #333',
                    borderRadius: '8px',
                    color: 'white',
                    fontSize: '14px',
                    boxSizing: 'border-box',
                    direction: 'rtl',
                    textAlign: 'right'
                  }}
                />
              </div>
            </>
          )}

          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 'bold', color: 'white' }}>
              {t('customers.edit.code')}
            </label>
            <input
              type="text"
              name="code"
              value={formData.code}
              onChange={handleChange}
              style={{
                width: '100%',
                padding: '12px',
                backgroundColor: '#1a1f2e',
                border: '1px solid #333',
                borderRadius: '8px',
                color: 'white',
                fontSize: '14px',
                boxSizing: 'border-box',
                direction: isRTL ? 'rtl' : 'ltr',
                textAlign: isRTL ? 'right' : 'left'
              }}
            />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 'bold', color: 'white' }}>
              {t('customers.edit.contactPerson')}
            </label>
            <input
              type="text"
              name="contact_person"
              value={formData.contact_person}
              onChange={handleChange}
              style={{
                width: '100%',
                padding: '12px',
                backgroundColor: '#1a1f2e',
                border: '1px solid #333',
                borderRadius: '8px',
                color: 'white',
                fontSize: '14px',
                boxSizing: 'border-box',
                direction: isRTL ? 'rtl' : 'ltr',
                textAlign: isRTL ? 'right' : 'left'
              }}
            />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 'bold', color: 'white' }}>
              {t('customers.edit.email')}
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              style={{
                width: '100%',
                padding: '12px',
                backgroundColor: '#1a1f2e',
                border: '1px solid #333',
                borderRadius: '8px',
                color: 'white',
                fontSize: '14px',
                boxSizing: 'border-box',
                direction: 'ltr',
                textAlign: 'left'
              }}
            />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 'bold', color: 'white' }}>
              {t('customers.edit.phone')}
            </label>
            <input
              type="text"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              style={{
                width: '100%',
                padding: '12px',
                backgroundColor: '#1a1f2e',
                border: '1px solid #333',
                borderRadius: '8px',
                color: 'white',
                fontSize: '14px',
                boxSizing: 'border-box',
                direction: isRTL ? 'rtl' : 'ltr',
                textAlign: isRTL ? 'right' : 'left'
              }}
            />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 'bold', color: 'white' }}>
              {t('customers.edit.address')}
            </label>
            <input
              type="text"
              name="address"
              value={formData.address}
              onChange={handleChange}
              style={{
                width: '100%',
                padding: '12px',
                backgroundColor: '#1a1f2e',
                border: '1px solid #333',
                borderRadius: '8px',
                color: 'white',
                fontSize: '14px',
                boxSizing: 'border-box',
                direction: isRTL ? 'rtl' : 'ltr',
                textAlign: isRTL ? 'right' : 'left'
              }}
            />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 'bold', color: 'white' }}>
              {t('customers.edit.taxNumber')}
            </label>
            <input
              type="text"
              name="tax_number"
              value={formData.tax_number}
              onChange={handleChange}
              style={{
                width: '100%',
                padding: '12px',
                backgroundColor: '#1a1f2e',
                border: '1px solid #333',
                borderRadius: '8px',
                color: 'white',
                fontSize: '14px',
                boxSizing: 'border-box',
                direction: isRTL ? 'rtl' : 'ltr',
                textAlign: isRTL ? 'right' : 'left'
              }}
            />
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '30px', direction: isRTL ? 'rtl' : 'ltr' }}>
            <input
              type="checkbox"
              name="is_active"
              checked={formData.is_active}
              onChange={handleChange}
              style={{
                width: '20px',
                height: '20px',
                cursor: 'pointer',
                margin: isRTL ? '0 0 0 10px' : '0 10px 0 0'
              }}
            />
            <label style={{ fontSize: '14px', fontWeight: 'bold', color: 'white', cursor: 'pointer' }}>
              {t('customers.edit.active')}
            </label>
          </div>
        </div>

        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 'bold', color: 'white' }}>
            {t('customers.edit.notes')}
          </label>
          <textarea
            name="notes"
            value={formData.notes}
            onChange={handleChange}
            rows="4"
            style={{
              width: '100%',
              padding: '12px',
              backgroundColor: '#1a1f2e',
              border: '1px solid #333',
              borderRadius: '8px',
              color: 'white',
              fontSize: '14px',
              boxSizing: 'border-box',
              resize: 'vertical',
              direction: isRTL ? 'rtl' : 'ltr',
              textAlign: isRTL ? 'right' : 'left'
            }}
          />
        </div>

        <div style={{ display: 'flex', gap: '15px', justifyContent: 'center', marginTop: '30px', flexDirection: isRTL ? 'row-reverse' : 'row' }}>
          <button
            type="button"
            onClick={() => navigate('/customers')}
            style={{
              backgroundColor: '#666',
              color: 'white',
              border: 'none',
              padding: '12px 24px',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: 'bold'
            }}
          >
            {t('customers.edit.cancel')}
          </button>
          <button
            type="submit"
            disabled={isLoading}
            style={{
              backgroundColor: isLoading ? '#666' : '#AC2000',
              color: 'white',
              border: 'none',
              padding: '12px 24px',
              borderRadius: '8px',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: 'bold',
              opacity: isLoading ? 0.7 : 1
            }}
          >
            {isLoading ? t('customers.edit.saving') : t('customers.edit.save')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default EditCustomer;

